import React, { Ref, useRef, useState } from 'react';
import {
  Pressable, ScrollView, ScrollViewProps, StyleSheet, View, Animated,
} from 'react-native';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import { useSharedValue } from 'react-native-reanimated';
import Colors from '../constants/Colors';
import Style from '../constants/Style';
import PressableStyled from './PressableStyled';
import DownArrow24 from './24pxIcons/DownArrow24';

const ScrollViewStyled = (props: ScrollViewProps) => {
  const [viewSize, setViewSize] = useState<number>(0);
  const [contentSize, setContentSize] = useState<number>(0);
  const [offset] = useState<Animated.Value>(new Animated.Value(0));
  const scrollView: Ref<ScrollView> = useRef<ScrollView>(null);
  const scrollable: boolean = contentSize > viewSize;
  const indicatorSize: number = scrollable ? viewSize * (viewSize / contentSize) : 0;
  // const translation: number = offset * (contentSize / viewSize)
  //   * ((viewSize - indicatorSize) / (contentSize - indicatorSize));
  const translation = scrollable ? Animated.multiply(offset, (contentSize / viewSize)).interpolate({
    inputRange: [0, contentSize - viewSize],
    outputRange: [0, viewSize - indicatorSize],
    extrapolate: 'clamp',
  }) : null;
  const scrollX = useSharedValue(0);
  const scrollY = useSharedValue(0);

  const onGestureEvent = Gesture.Pan()
    .onChange((event) => {
      scrollX.value += event.changeX;
      scrollY.value += event.changeY;
      scrollView?.current?.scrollTo({ x: scrollX.value, y: scrollY.value, animated: false });
    });

  if (props.horizontal) {
    const [scrollPosition, setScrollPosition] = useState<number>(0);

    const scroll = (multiplier: number) => scrollView?.current
      ?.scrollTo({ x: scrollPosition + contentSize * 0.2 * multiplier, animated: true });

    return <View style={[Style.row, styles.horizontalContainer]}>
      <PressableStyled
        disabled={!scrollable || scrollPosition === 0}
        style={styles.button}
        onLongPress={() => scrollView?.current?.scrollTo({ x: 0, animated: true })}
        onPress={() => scroll(-1)}
      >
        <DownArrow24
          color={Colors.light.hereworksBlack400}
          style={{ transform: [{ rotate: '90deg' }] }}
        />
      </PressableStyled>
      <ScrollView
        ref={scrollView}
        horizontal
        showsHorizontalScrollIndicator={false}
        onContentSizeChange={(width: number) => setContentSize(width)}
        onScroll={({ nativeEvent: { contentOffset: { x } } }) => setScrollPosition(x)}
        onLayout={({ nativeEvent: { layout: { width } } }) => setViewSize(width)}
        style={styles.horizontalContainer}
        contentContainerStyle={styles.horizontalContent}
        {...props}
      />
      <PressableStyled
        disabled={!scrollable || scrollPosition >= contentSize - viewSize}
        style={styles.button}
        onLongPress={() => scrollView?.current?.scrollToEnd()}
        onPress={() => scroll(1)}
      >
        <DownArrow24
          color={Colors.light.hereworksBlack400}
          style={{ transform: [{ rotate: '270deg' }] }}
        />
      </PressableStyled>
    </View>;
  }

  return <GestureHandlerRootView
    style={[!props.horizontal && styles.verticalContainer, props.style]}
  >
    <ScrollView
      ref={scrollView}
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
      onContentSizeChange={(width: number, height: number) => setContentSize(props.horizontal
        ? width : height)}
      onLayout={({ nativeEvent: { layout: { width, height } } }) => setViewSize(props.horizontal
        ? width : height)}
      // onScroll={({ nativeEvent: { contentOffset: { x, y } } }) => setOffset(props
      //   .horizontal ? x : y)}
      onScroll={Animated.event(
        props.horizontal ? [{ nativeEvent: { contentOffset: { x: offset } } }]
          : [{ nativeEvent: { contentOffset: { y: offset } } }],
        { useNativeDriver: false },
      )}
      {...props}
      // contentOffset={{ x: props.horizontal ? offset : 0, y: props.horizontal ? 0 : offset }}
      scrollEventThrottle={1}
      style={[]}
      children={props.children}
    />
    {(props.horizontal || scrollable) && <View style={props.horizontal
      ? styles.indicatorWrapperHorizontal : styles.indicatorWrapperVertical}>
      <Animated.View style={[
        props.horizontal ? styles.indicatorHorizontal : styles.indicatorVertical, {
          width: props.horizontal ? indicatorSize : undefined,
          height: props.horizontal ? undefined : indicatorSize,
          transform: [
            { translateX: props.horizontal ? translation : 1 },
            { translateY: props.horizontal ? 1 : translation },
          ],
        }]}>
        <GestureDetector gesture={onGestureEvent}>
          <Pressable style={{ height: '100%', width: '100%' }} />
        </GestureDetector>
      </Animated.View>
    </View>}
  </GestureHandlerRootView>;
};

const styles = StyleSheet.create({
  horizontalContainer: {
    flex: 1,
    alignSelf: 'stretch',
  },
  horizontalContent: {
    minWidth: '100%',
  },
  verticalContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  indicatorWrapperHorizontal: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    height: 6,
    borderRadius: 3,
  },
  indicatorWrapperVertical: {
    position: 'absolute',
    end: 2,
    alignSelf: 'stretch',
    height: '100%',
    width: 6,
    borderRadius: 3,
  },
  indicatorHorizontal: {
    borderRadius: 3,
    height: '100%',
    backgroundColor: Colors.light.hereworksBlue100,
    minWidth: 24,
  },
  indicatorVertical: {
    borderRadius: 3,
    width: '100%',
    backgroundColor: Colors.light.hereworksBlue100,
    minHeight: 24,
  },
  button: {
    width: 36,
    height: 36,
    margin: 16,
    padding: 6,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlack100,
    backgroundColor: Colors.light.hereworksBlack50,
    borderRadius: 18,
    shadowColor: '#001F31',
    shadowOpacity: 0.1,
    shadowRadius: 20,
  },
});

export default ScrollViewStyled;
